import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { base64ToBlob } from 'src/app/common/utils/base64-to-blob';
import { download } from 'src/app/common/utils/downloadFile';
import { DownloadService } from 'src/app/data/download.service';
import { FileRequest } from 'src/app/models/FileRequest';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-custom-multiple-file-viewer',
  templateUrl: './custom-multiple-file-viewer.component.html',
  styleUrls: ['./custom-multiple-file-viewer.component.scss'],
  standalone: false
})
export class CustomMultipleFileViewerComponent implements OnInit, OnDestroy {
  @ViewChild('printf') iframe: ElementRef;

  files: FileRequest[] = [];
  selectedFile: FileRequest;

  fileName: string = null;
  url: string;
  imageUrl: SafeUrl;
  srcUrl: SafeResourceUrl;
  isPrinting: boolean = false;
  isLoading: boolean = false;
  isPdf: boolean = false;
  isImage: boolean = false;
  currentPage = 1;
  totalPages: number;

  constructor(
    private domSanitizer: DomSanitizer,
    private downloadService: DownloadService,
    private dialogRef: MatDialogRef<CustomMultipleFileViewerComponent>,
    @Inject(MAT_DIALOG_DATA) private data: FileRequest[]
  ) {
    this.files = this.data;
  }

  ngOnInit(): void {
    if (!this.files || this.files.length === 0) {
      this.dialogRef.close();
      return;
    }
    if (this.files && this.files.length > 0) {
      this.onImageChange(this.files[0]);
    }
  }

  onImageChange(file: FileRequest): void {
    this.selectedFile = file;
    const extension = (file.FileName as any).getFileExtension()?.toLowerCase();
    this.isPdf = extension === 'pdf';
    this.isImage = ['png', 'jpeg', 'jpg', 'webp'].includes(extension);
    this.currentPage = 1;

    if (this.isPdf) {
      this.dialogRef.updateSize('60vw');
    } else {
      this.dialogRef.updateSize();
    }

    if (this.isPdf || this.isImage) {
      this.loadFile(this.selectedFile);
    } else {
      this.revokePreviousUrls();
      this.url = null;
      this.srcUrl = null;
      this.imageUrl = null;
      this.isPdf = false;
      this.isImage = false;
    }
  }

  loadFile(file: FileRequest): void {
    this.isLoading = true;
    this.fileName = file.FileName;
    if (file.Endpoint && file.FileId) {
      this.downloadService.getFileAsBlob(file.Endpoint, file.FileId, file.FileName).subscribe(
        (srcUrl: any) => {
          this.revokePreviousUrls();
          this.url = srcUrl;
          this.srcUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(srcUrl);
          if (this.isImage) {
            this.imageUrl = this.domSanitizer.bypassSecurityTrustUrl(srcUrl);
          }
          this.isLoading = false;
        },
        (err) => {
          this.isLoading = false;
          this.dialogRef.close();
        }
      );
    } else if (file.Content) {
      this.revokePreviousUrls();
      const extension = (file.FileName as any).getFileExtension()?.toLowerCase();
      const fileType = this.isPdf ? 'application/pdf' : `image/${extension}`;
      const srcUrl = window.URL.createObjectURL(base64ToBlob(file.Content, fileType));
      this.url = srcUrl;
      this.srcUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(srcUrl);
      if (this.isImage) {
        this.imageUrl = this.domSanitizer.bypassSecurityTrustUrl(srcUrl);
      }
      this.isLoading = false;
    }
  }

  revokePreviousUrls(): void {
    if (this.url) {
      window.URL.revokeObjectURL(this.url);
    }
    if (this.imageUrl) {
      const unsafeUrl = this.domSanitizer.sanitize(4, this.imageUrl);
      if (unsafeUrl) window.URL.revokeObjectURL(unsafeUrl);
    }
    if (this.srcUrl) {
      const unsafeUrl = this.domSanitizer.sanitize(5, this.srcUrl);
      if (unsafeUrl) window.URL.revokeObjectURL(unsafeUrl);
    }
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.revokePreviousUrls();
    });
  }

  downloadFile = (): void => download(this.url, this.fileName);

  printFile(): void {
    this.isPrinting = true;
    const frame = this.iframe?.nativeElement as HTMLIFrameElement;

    if (!frame) {
      setTimeout(() => this.printFile(), 1000);
      return;
    }

    frame.focus();

    if (this.isInternetExplorerBrowser() || this.isEdgeBrowser()) {
      frame.contentWindow.document.execCommand('print', false, null);
    } else {
      frame.contentWindow.print();
    }
  }

  close = (): void => this.dialogRef.close();

  afterLoadComplete(pdf: PDFDocumentProxy): void {
    this.totalPages = pdf.numPages;
  }

  private isInternetExplorerBrowser(): boolean {
    return navigator.userAgent.lastIndexOf('MSIE') !== -1;
  }

  private isEdgeBrowser(): boolean {
    return !this.isInternetExplorerBrowser() && !!(window as any).StyleMedia;
  }
}